/**
 * Safe localStorage utility functions with quota handling
 */

/**
 * Safely set item in localStorage with quota handling
 * @param {string} key - The key to store the item under
 * @param {string} value - The value to store (must be a string)
 * @param {Function} onError - Optional error callback
 * @returns {boolean} - True if successful, false if failed
 */
export const safeSetLocalStorage = (key, value, onError = null) => {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (error) {
        if (error.name === "QuotaExceededError") {
            console.warn("localStorage quota exceeded for key:", key);

            // Try to clear some space by removing old data
            try {
                // Clear the same key if it exists (to replace with new data)
                localStorage.removeItem(key);

                // Try to clear other non-essential data if needed
                clearNonEssentialData();

                // Try again
                localStorage.setItem(key, value);
                console.log("Successfully saved after clearing space");
                return true;
            } catch (retryError) {
                console.error("Failed to save even after clearing space:", retryError);
                if (onError) {
                    onError("Storage full. Please clear browser data or reduce file sizes.");
                }
                return false;
            }
        } else {
            console.error("localStorage error:", error);
            if (onError) {
                onError("Unable to save data locally.");
            }
            return false;
        }
    }
};

/**
 * Safely get item from localStorage
 * @param {string} key - The key to retrieve
 * @param {*} defaultValue - Default value if key doesn't exist or fails
 * @returns {string|null} - The stored value or default
 */
export const safeGetLocalStorage = (key, defaultValue = null) => {
    try {
        const value = localStorage.getItem(key);
        return value !== null ? value : defaultValue;
    } catch (error) {
        console.error("Failed to retrieve from localStorage:", error);
        return defaultValue;
    }
};

/**
 * Safely parse JSON from localStorage
 * @param {string} key - The key to retrieve and parse
 * @param {*} defaultValue - Default value if parsing fails
 * @returns {*} - Parsed object or default value
 */
export const safeGetParsedLocalStorage = (key, defaultValue = null) => {
    try {
        const value = safeGetLocalStorage(key);
        if (value) {
            return JSON.parse(value);
        }
        return defaultValue;
    } catch (error) {
        console.error("Failed to parse JSON from localStorage:", error);
        // Clear corrupted data
        localStorage.removeItem(key);
        return defaultValue;
    }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - The key to remove
 */
export const safeRemoveLocalStorage = (key) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error("Failed to remove from localStorage:", error);
    }
};

/**
 * Clear non-essential data from localStorage to free up space
 * You can customize this based on your app's needs
 */
const clearNonEssentialData = () => {
    try {
        // List of non-essential keys that can be cleared to free up space
        const nonEssentialKeys = ["tempData", "cache_", "temp_", "preview_"];

        // Clear items that start with non-essential prefixes
        Object.keys(localStorage).forEach((key) => {
            if (nonEssentialKeys.some((prefix) => key.startsWith(prefix))) {
                localStorage.removeItem(key);
            }
        });

        console.log("Cleared non-essential localStorage data");
    } catch (error) {
        console.error("Failed to clear non-essential data:", error);
    }
};

/**
 * Get localStorage usage information
 * @returns {Object} - Usage statistics
 */
export const getLocalStorageUsage = () => {
    try {
        let totalSize = 0;
        const itemSizes = {};

        for (let key in localStorage) {
            if (localStorage.hasOwnProperty(key)) {
                const value = localStorage.getItem(key);
                const size = new Blob([value]).size;
                totalSize += size;
                itemSizes[key] = size;
            }
        }

        // Approximate quota (varies by browser, typically 5-10MB)
        const estimatedQuota = 5 * 1024 * 1024; // 5MB estimate

        return {
            totalSize,
            itemSizes,
            estimatedQuota,
            usagePercentage: (totalSize / estimatedQuota) * 100,
        };
    } catch (error) {
        console.error("Failed to calculate localStorage usage:", error);
        return null;
    }
};
